import { isEmpty } from 'lodash'
import * as dateFns from 'date-fns'
import { EuCountries } from 'todistukset-common/src/vtj/EuCountries.enum'
import {
  ErrorTranslationParams,
  ValidationError,
  ValueValidator,
} from 'todistukset-common/src/vtj/form-validation.util'
import {
  availableTranslations,
  availableUAETTodistusLanguages,
} from 'todistukset-common/src/vtj/language.util'
import {
  finalEditableFieldValue,
  isEditableField,
} from 'todistukset-common/src/vtj/types/editable-field.type'
import { UaetUiForm } from 'todistukset-common/src/vtj/types/form-types.type'
import {
  MAX_LENGTH_OF_MAIL_DELIVERY_FIELD,
  ValidateParams,
  validate,
  validateTodistusLanguage,
  validateContactEmail,
  validateContactPhoneNumber,
  validateMaritalStatus,
  propertyNameTranslationKeysAetAndUaetForms,
} from 'todistukset-common/src/vtj/validation'
import { DATE_FORMAT } from 'todistukset-common/src/vtj/util/date.util'
import { getDateOfMajority } from 'common/src/vtj/ssn'
import { VtjCountryCodeAdditions } from 'todistukset-common/src/vtj/vtj-koodisto.util'
import { MaritalStatus } from 'todistukset-common/src/vtj/MaritalStatus.enum'

export type UaetFormPropertyName =
  | keyof UaetUiForm
  | 'translationFormLanguage.ilmoitettuArvo'
  | 'spouseA.maritalStatus.ilmoitettuArvo'
  | 'spouseA.contactInfo.phoneNumber.ilmoitettuArvo'
  | 'spouseA.contactInfo.email.ilmoitettuArvo'
  | 'mailDelivery.firstName'
  | 'mailDelivery.lastName'
  | 'mailDelivery.streetAddress'
  | 'mailDelivery.streetAddressExtras'
  | 'mailDelivery.postalCode'
  | 'mailDelivery.postOffice'
  | 'mailDelivery.country'
  | 'spouseA.sukulaisuussuhdeVakuutettu.ilmoitettuArvo'
  | 'spouseB.firstNames.ilmoitettuArvo'
  | 'spouseB.lastName.ilmoitettuArvo'
  | 'spouseB.birthdate.ilmoitettuArvo'
  | 'spouseB.citizenshipCode.ilmoitettuArvo'
  | 'spouseB.birthPlace.ilmoitettuArvo'
  | 'spouseB.birthCountryCode.ilmoitettuArvo'
  | 'spouseB.maritalStatus.ilmoitettuArvo'

type UaetFormValueValidator = ValueValidator<
  Partial<{ [Key in UaetFormPropertyName]: unknown }>
>
export type UaetFormValueValidatorNoUser = (
  value: Parameters<UaetFormValueValidator>[0],
  form: Parameters<UaetFormValueValidator>[1]
) => ReturnType<UaetFormValueValidator>
export type UaetFormValueValidatorNoUserNoForm = (
  value: Parameters<UaetFormValueValidator>[0]
) => ReturnType<UaetFormValueValidator>

export const validateUaetOrdererInfoStep = (
  params: ValidateParams
): ValidationError[] => {
  return validate(params)([
    [validateMaritalStatus, 'spouseA.maritalStatus.ilmoitettuArvo'],
  ])
}

export const validateUaetSpouseInfoStep = (
  params: ValidateParams
): ValidationError[] => {
  return validate(params)([
    [validateSpouseBFirstNames, 'spouseB.firstNames.ilmoitettuArvo'],
    [validateSpouseBLastName, 'spouseB.lastName.ilmoitettuArvo'],
    [validateSpouseBBirthDate, 'spouseB.birthdate.ilmoitettuArvo'],
    [validateSpouseBCitizenshipCode, 'spouseB.citizenshipCode.ilmoitettuArvo'],
    [validateSpouseBBirthPlace, 'spouseB.birthPlace.ilmoitettuArvo'],
    [validateSpouseBBirthCountry, 'spouseB.birthCountryCode.ilmoitettuArvo'],
    [validateSpouseBMaritalStatus, 'spouseB.maritalStatus.ilmoitettuArvo'],
    [
      validateSpouseNotRelated,
      'spouseA.sukulaisuussuhdeVakuutettu.ilmoitettuArvo',
    ],
  ])
}

export const validateUaetUsageRegionStep = (
  params: ValidateParams
): ValidationError[] => {
  return validate(params)([
    [validateUaetPlaceOfMarriage, 'placeOfMarriage.ilmoitettuArvo'],
  ])
}

export const validateUaetAdditionalSelectionStep = (
  params: ValidateParams
): ValidationError[] => {
  return validate(params)([
    [validateUaetTodistusLanguage, 'todistusLanguage.ilmoitettuArvo'],
    [validateTranslationFormLanguage, 'translationFormLanguage.ilmoitettuArvo'],
  ])
}

export const validateUaetContactInfoStep = (
  params: ValidateParams
): ValidationError[] => {
  return validate(params)([
    [validateUaetMailDeliveryFirstName, 'mailDelivery.firstName'],
    [validateUaetMailDeliveryLastName, 'mailDelivery.lastName'],
    [validateUaetMailDeliveryStreetAddress, 'mailDelivery.streetAddress'],
    [
      validateUaetMailDeliveryStreetAddressExtras,
      'mailDelivery.streetAddressExtras',
    ],
    [validateUaetMailDeliveryPostalCode, 'mailDelivery.postalCode'],
    [validateUaetMailDeliveryPostOffice, 'mailDelivery.postOffice'],
    [validateUaetMailDeliveryCountry, 'mailDelivery.country'],
    [
      validateContactPhoneNumber,
      'spouseA.contactInfo.phoneNumber.ilmoitettuArvo',
    ],
    [validateContactEmail, 'spouseA.contactInfo.email.ilmoitettuArvo'],
  ])
}

function isMissingString(value: unknown) {
  if (typeof value !== 'string') {
    return true
  }
  if (value.trim().length < 1) {
    return true
  }
  return false
}

function isMissingListItem(value: unknown) {
  if (!Array.isArray(value)) {
    return true
  }
  if (value.length < 1) {
    return true
  }
  return false
}

const createValidationError =
  (propertyName: UaetFormPropertyName) =>
  (
    errorTranslationKey: string,
    errorTranslationParams: ErrorTranslationParams = {}
  ): ValidationError => {
    const field =
      propertyNameTranslationKeysAetAndUaetForms[propertyName] ?? propertyName
    return {
      propertyName,
      errorTranslationKey,
      errorTranslationParams: { field, ...errorTranslationParams },
    }
  }

const validateSpouseInfoString = (
  field:
    | 'spouseB.firstNames.ilmoitettuArvo'
    | 'spouseB.lastName.ilmoitettuArvo'
    | 'spouseB.birthPlace.ilmoitettuArvo',
  value: unknown
) => {
  const error = createValidationError(field)
  if (isMissingString(value) || typeof value !== 'string') {
    return error('validationRequiredValueMissing')
  }
  if (value.length > 100) {
    return error('validationLengthExceedsMaximum', { count: String(100) })
  }
}

export const validateSpouseBFirstNames: UaetFormValueValidatorNoUserNoForm = (
  value
) => validateSpouseInfoString('spouseB.firstNames.ilmoitettuArvo', value)

export const validateSpouseBLastName: UaetFormValueValidatorNoUserNoForm = (
  value
) => validateSpouseInfoString('spouseB.lastName.ilmoitettuArvo', value)

export const validateSpouseBBirthDate: UaetFormValueValidatorNoUserNoForm = (
  value
) => {
  const error = createValidationError('spouseB.birthdate.ilmoitettuArvo')
  if (isMissingString(value) || typeof value !== 'string') {
    return error('validationRequiredValueMissing')
  }
  const parsed = dateFns.parse(value.trim(), DATE_FORMAT, new Date())
  if (!dateFns.isValid(parsed)) {
    return error('validationInvalidFinnishFormatDate')
  }
  const adultAt = dateFns.startOfDay(getDateOfMajority(parsed))
  if (dateFns.startOfDay(new Date()) < adultAt) {
    return error('validationSpouseHetuUnderage')
  }
}

export const validateSpouseBCitizenshipCode: UaetFormValueValidatorNoUserNoForm =
  (value) => {
    const error = createValidationError(
      'spouseB.citizenshipCode.ilmoitettuArvo'
    )
    if (isMissingListItem(value) || !Array.isArray(value)) {
      return error('validationRequiredValueMissing')
    }
    if (value.length > 20) {
      return error('validationCitizenshipListTooLong', { max: 20 })
    }
    if (
      value.includes(VtjCountryCodeAdditions.KANSALAISUUDETON) &&
      value.length > 1
    ) {
      return error('validationCitizenshipStatelessWithOthers')
    }
  }

export const validateSpouseBBirthPlace: UaetFormValueValidatorNoUserNoForm = (
  value
) => validateSpouseInfoString('spouseB.birthPlace.ilmoitettuArvo', value)

export const validateSpouseBBirthCountry: UaetFormValueValidatorNoUserNoForm = (
  value
) => {
  if (isMissingString(value)) {
    return createValidationError('spouseB.birthCountryCode.ilmoitettuArvo')(
      'validationRequiredValueMissing'
    )
  }
}

export const validateSpouseBMaritalStatus: UaetFormValueValidatorNoUserNoForm =
  (value) => {
    const error = createValidationError('spouseB.maritalStatus.ilmoitettuArvo')
    if (isMissingString(value)) {
      return error('validationRequiredValueMissing')
    }
    if (
      !Array.from<unknown>([
        MaritalStatus.NAIMATON,
        MaritalStatus.ERONNUT,
        MaritalStatus.LESKI,
      ]).includes(value)
    ) {
      // The above are the only options that the UI offers
      return error('validationUnknownError')
    }
  }

export const validateSpouseNotRelated: UaetFormValueValidatorNoUserNoForm = (
  value
) => {
  if (value !== true) {
    return createValidationError(
      'spouseA.sukulaisuussuhdeVakuutettu.ilmoitettuArvo'
    )('validationSpouseNotRelated')
  }
}

export const validateUaetPlaceOfMarriage: UaetFormValueValidatorNoUserNoForm = (
  value
) => {
  if (isEmpty(value)) {
    return createValidationError('placeOfMarriage')(
      'validationMissingPlaceOfWedding'
    )
  }

  if (value === EuCountries.FINLAND) {
    return createValidationError('placeOfMarriage')(
      'aetOrdererAdditionalSelectionsWrongCertificateTitle'
    )
  }
}

export const validateUaetTodistusLanguage: UaetFormValueValidatorNoUserNoForm =
  (value) => validateTodistusLanguage(value, availableUAETTodistusLanguages)

export const validateTranslationFormLanguage: UaetFormValueValidatorNoUser = (
  value,
  form
) => {
  const validationError = createValidationError('translationFormLanguage')

  const includeTranslationForm = !!(
    isEditableField(form.includeTranslationForm) &&
    finalEditableFieldValue(form.includeTranslationForm)
  )

  if (!includeTranslationForm) {
    return undefined
  }

  if (!value) {
    return validationError('validationRequiredValueMissing')
  }
  if (!(availableTranslations as Readonly<unknown[]>).includes(value)) {
    return validationError('validationUnknownError')
  }
}

export const validateUaetMailDeliveryFirstName: UaetFormValueValidatorNoUserNoForm =
  (value) => {
    return validateMailDeliveryField(value, 'mailDelivery.firstName')
  }

export const validateUaetMailDeliveryLastName: UaetFormValueValidatorNoUserNoForm =
  (value) => {
    return validateMailDeliveryField(value, 'mailDelivery.lastName')
  }

export const validateUaetMailDeliveryStreetAddress: UaetFormValueValidatorNoUserNoForm =
  (value) => {
    return validateMailDeliveryField(value, 'mailDelivery.streetAddress')
  }

export const validateUaetMailDeliveryStreetAddressExtras: UaetFormValueValidatorNoUserNoForm =
  (value) => {
    return validateMailDeliveryField(
      value,
      'mailDelivery.streetAddressExtras',
      true
    )
  }

export const validateUaetMailDeliveryPostalCode: UaetFormValueValidatorNoUserNoForm =
  (value) => {
    return validateMailDeliveryField(value, 'mailDelivery.postalCode')
  }

export const validateUaetMailDeliveryPostOffice: UaetFormValueValidatorNoUserNoForm =
  (value) => {
    return validateMailDeliveryField(value, 'mailDelivery.postOffice')
  }

export const validateUaetMailDeliveryCountry: UaetFormValueValidatorNoUserNoForm =
  (value) => {
    return validateMailDeliveryField(value, 'mailDelivery.country')
  }

const validateMailDeliveryField = (
  value: unknown,
  propertyName: UaetFormPropertyName,
  optional?: boolean
) => {
  const finalValue = isEditableField(value) ? value.ilmoitettuArvo : value

  if (optional && isEmpty(finalValue)) {
    return
  }

  const validationError = createValidationError(propertyName)

  if (isEmpty(finalValue)) {
    return validationError('validationMailDeliveryRequiredValueMissing')
  }

  if (typeof finalValue !== 'string') {
    return validationError('validationUnknownError')
  }

  if (finalValue.length > MAX_LENGTH_OF_MAIL_DELIVERY_FIELD) {
    return validationError('validationMailDeliveryLengthExceedsMaximum', {
      count: MAX_LENGTH_OF_MAIL_DELIVERY_FIELD,
    })
  }
}
